import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import type MapFloorModel from 'garaje/models/map-floor';
import type NeighborhoodModel from 'garaje/models/neighborhood';
import type WorkplaceDayModel from 'garaje/models/workplace-day';
import type StateService from 'garaje/services/state';

import type SpacesMapsLiveNewController from './controller';

export interface ModelResponse {
  mapFloors: MapFloorModel[];
  neighborhoods: NeighborhoodModel[];
  workplaceDays: WorkplaceDayModel[];
}

/**
 * Loads the collections the live-new toolbar needs: floors (floor toggle),
 * neighborhoods (dropdown), and workplace days (date/time picker's working-
 * hours awareness). Kept minimal — the map itself is rendered by the
 * embedded floor-plan-fe iframe, which loads its own data from
 * floor-plan-service. Garaje only owns the toolbar chrome plus the state
 * that drives the iframe URL.
 */
export default class SpacesMapsLiveNewRoute extends Route {
  @service declare store: Store;
  @service declare state: StateService;

  async model(): Promise<ModelResponse> {
    const locationId = this.state.currentLocation?.id;
    if (!locationId) {
      return { mapFloors: [], neighborhoods: [], workplaceDays: [] };
    }

    const [mapFloorsRes, neighborhoodsRes, workplaceDays] = await Promise.all([
      this.store.query('map-floor', { filter: { location: locationId } }),
      this.store.query('neighborhood', {
        filter: { 'location-id': locationId },
      }),
      this.fetchWorkplaceDays(),
    ]);

    return {
      mapFloors: mapFloorsRes.slice(),
      neighborhoods: neighborhoodsRes.slice(),
      workplaceDays,
    };
  }

  /**
   * Mirrors live/show/route.ts:fetchWorkplaceDays — loads the desk-location's
   * workplace days so MapDateSelect knows the working-hours window. Returns
   * an empty array when the current location doesn't have desks enabled.
   */
  async fetchWorkplaceDays(): Promise<WorkplaceDayModel[]> {
    const deskLocationId = this.state.currentLocationEnabledDesks?.id;
    if (!deskLocationId) return [];

    await this.store.findRecord('desk-location', deskLocationId, {
      reload: true,
      include: 'workplace-days',
    });

    return this.store
      .peekAll('workplaceDay')
      .filter((day: WorkplaceDayModel) => day.belongsTo('deskLocation').id() === deskLocationId) as WorkplaceDayModel[];
  }

  /**
   * Reconcile a bookmarked `selectedFloorId` query param against the
   * floors actually loaded for the current location. If the persisted
   * id doesn't match a real floor (renamed, deleted, wrong location),
   * clear it so the URL, the FloorToggle, and the iframe src all agree
   * — the controller's `currentFloor` getter then falls back to
   * `sortedMapFloors[0]` cleanly.
   */
  setupController(controller: SpacesMapsLiveNewController, model: ModelResponse, transition: Transition): void {
    super.setupController(controller, model, transition);

    const persisted = controller.selectedFloorId;
    if (persisted && !model.mapFloors.some((f) => f.id === persisted)) {
      controller.selectedFloorId = null;
    }
  }
}
